import { useQuery } from '@tanstack/react-query';
import type { BoardResponse } from '../services/api/board';
import { fetchBoardInternal } from '../services/api/board';
import { fetchBoardExternal } from '../services/api/externalBoard';
import { boardQueryKey, type BoardAudience } from './boardQueryKey';

// MKS-419 — re-exported so callers can keep importing the key helper (and the
// audience type) from the hook module.
export { boardQueryKey };
export type { BoardAudience };

/**
 * Phase 24 Plan 24-03 — board data hook.
 *
 * Query key MUST be three-element ['board', projectId, audience] so internal
 * and external caches never collide (Pitfall 6 / D-12). Internal route uses
 * the api/client.ts transport; external uses direct fetch (Landmine #8).
 */
export function useBoard(
  projectId: string,
  audience: BoardAudience,
  opts?: { includeSubprojects?: boolean },
) {
  // MKS-419 — the external audience never has a merged board.
  const includeSubprojects = audience === 'internal' && opts?.includeSubprojects === true;
  return useQuery<BoardResponse>({
    queryKey: boardQueryKey(projectId, audience, includeSubprojects),
    queryFn:
      audience === 'internal'
        ? () =>
            includeSubprojects
              ? fetchBoardInternal(projectId, { includeSubprojects })
              : fetchBoardInternal(projectId)
        : () => fetchBoardExternal(projectId),
    enabled: Boolean(projectId),
    refetchInterval: 5000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}
